import { useRef } from 'react'
import { logDocumentViewed } from '@/apis/logging/document_views.ts'
import { FamilyApplicationDetails } from '@/queries/family_applications/requests.ts'

type LoggingConfigs =
  | {
      logType: 'family'
      applicantId: number | null
      documentsViewed: FamilyApplicationDetails['family_documents']
      docsViewedType: 'IDENTITY' | 'TAX'
    }
  | {
      logType: 'student'
      applicantId: number | null
      studentId: number
    }

/**
 * A hook that provides a function to log the viewing of application documents based on given logging configurations.
 * The function returned by this hook manages the logging process and ensures logging is not repeated unnecessarily.
 *
 * @param {LoggingConfigs} loggingConfigs - An object containing configuration options for document logging, including log type, applicant ID, specific documents viewed, and document type filters.
 * @return {Function} A function that performs the logging process when invoked. It ensures logging continuity and checks conditions before logging.
 */
export function useApplicationDocumentLogging(
  loggingConfigs: LoggingConfigs
): () => void {
  const didLog = useRef(false)
  if (loggingConfigs.logType === 'family') {
    return function () {
      // Handle no log cases
      if (didLog.current) return
      if (loggingConfigs.applicantId == undefined) return

      if (loggingConfigs.documentsViewed) {
        loggingConfigs.documentsViewed
          .filter((document) => document.type === loggingConfigs.docsViewedType)
          .forEach((doc) => {
            logDocumentViewed(loggingConfigs.applicantId!, doc.id)
              .then()
              .catch()
          })

        didLog.current = true
      }

      return
    }
  } else {
    return function () {
      if (didLog.current) return
      if (loggingConfigs.applicantId == undefined) return
      if (loggingConfigs.studentId == undefined) return

      logDocumentViewed(
        loggingConfigs.applicantId,
        undefined,
        loggingConfigs.studentId
      )
        .then()
        .catch()

      didLog.current = true
      return
    }
  }
}
